import * as THREE from 'three';
import TWEEN from '@tweenjs/tween.js';
import {USE_LEGACY_TWEEN_GROUP} from 'components/customer/Preview3DCommon/lib/tween';
import {AnimationHandler} from 'components/customer/Preview3D/animation/entity/AnimationHandler';
import {PreviewLocation} from 'components/customer/Preview3D/types';

/**
 * Creates an animation handler for a high-swing door animation.
 *
 * Positions the mesh correctly to pivot around its hinge, then provides
 * methods to lift, swing, and reset the door along an arc motion.
 *
 * The caller is responsible for setting the mesh's initial position and scale
 * before invoking this handler.
 *
 * @param {THREE.Group<THREE.Object3DEventMap>} mesh - The 3D mesh group representing the door.
 * @param {'y' | 'z'} [liftAxis='y'] - The axis along which the door lifts and pivots.
 *   Use 'y' when Y is the vertical axis (standard orientation),
 *   or 'z' when Z is the vertical axis (top-hang orientation in certain scenes).
 * @param {PreviewLocation} [previewLocation='SPECS'] - Current preview tab location.
 * @param {boolean} isDoorOpen - Whether the door is initially open.
 * @return {AnimationHandler} An object containing `open` and `close` animation step arrays.
 */
export const highSwingAnimationHandler = (
    mesh: THREE.Group<THREE.Object3DEventMap>,
    liftAxis: 'y' | 'z' = 'y',
    previewLocation: PreviewLocation = 'SPECS'
): AnimationHandler => {
    const bbox = new THREE.Box3().setFromObject(mesh);
    const size = new THREE.Vector3();
    bbox.getSize(size);

    const degrees = 65;
    const depthAxis = liftAxis === 'y' ? 'z' : 'y';

    const pivot = mesh.position.clone();

    const increment = liftAxis === 'y' ? 0.1 : -0.3;
    const r = (liftAxis === 'y' ? size.y : size.z) * increment;

    const closedRad = Math.PI / 2;
    const openRad = closedRad - THREE.MathUtils.degToRad(degrees);

    const depthInset = size.z * -5;
    const liftDistance = size.z * 0.01;
    const inwardClearance = liftAxis === 'y' ? -size.z / 2 : -size.z / 5;

    if (previewLocation === 'DOORS') {
        mesh.position.set(0, size.y, 0);
    }

    /**
     * Lifts the mesh slightly before the swing along the liftAxis.
     *
     * @param {number} [speed=100] - Duration of the lift tween in milliseconds.
     * @return {Promise<void>} Resolves when the lift-up animation completes.
     */
    function liftUp(speed = 100): Promise<void> {
        return new Promise((resolve) => {
            const to =
                liftAxis === 'y'
                    ? {y: mesh.position.y + liftDistance}
                    : {z: mesh.position.z + liftDistance};
            new TWEEN.Tween(mesh.position, USE_LEGACY_TWEEN_GROUP)
                .to(to, speed)
                .easing(TWEEN.Easing.Quadratic.Out)
                .onComplete(() => resolve())
                .start();
        });
    }

    /**
     * Animates the mesh along an arc to swing open or close.
     *
     * @param {number} from - The starting angle in radians.
     * @param {number} to - The ending angle in radians.
     * @param {number} speed - Duration of the arc tween in milliseconds.
     * @param {boolean} [closing=false] - Whether this motion is for closing.
     * @return {Promise<void>} Resolves when the arc motion completes.
     */
    function arcTween(
        from: number,
        to: number,
        speed: number,
        closing = false
    ): Promise<void> {
        return new Promise((resolve) => {
            const state = {a: from};
            new TWEEN.Tween(state, USE_LEGACY_TWEEN_GROUP)
                .to({a: to}, speed)
                .easing(
                    closing ? TWEEN.Easing.Cubic.Out : TWEEN.Easing.Cubic.InOut
                )
                .onUpdate(() => {
                    const angle = state.a;
                    const delta = angle - closedRad;
                    const progress =
                        (closedRad - angle) / (closedRad - openRad);

                    mesh.rotation.x = angle;
                    const liftPos = Math.sin(delta) * r;
                    if (liftAxis === 'y') {
                        mesh.position.y = pivot.y + liftPos;
                    } else {
                        mesh.position.z = pivot.z + liftPos;
                    }

                    const extraDepth =
                        liftAxis === 'y'
                            ? (1 - Math.cos(delta)) * r - depthInset * progress
                            : 0;
                    const depthPos =
                        (depthAxis === 'y' ? pivot.y : pivot.z) +
                        extraDepth -
                        inwardClearance * progress;
                    if (depthAxis === 'y') {
                        mesh.position.y = depthPos;
                    } else {
                        mesh.position.z = depthPos;
                    }
                })
                .onComplete(() => resolve())
                .start();
        });
    }

    /**
     * Slides the mesh outward slightly after swinging open.
     *
     * @param {number} [speed=500] - Duration of the slide-out tween in milliseconds.
     * @return {Promise<void>} Resolves when the slide-out animation completes.
     */
    function slideOutY(speed = 500): Promise<void> {
        return new Promise((resolve) => {
            const to =
                liftAxis === 'y'
                    ? {
                          y: mesh.position.y + size.y * 0.3,
                          z: mesh.position.z + size.z * 0.1,
                      }
                    : {
                          y: mesh.position.y + size.z * 0.1,
                          z: mesh.position.z + size.y * 0.3,
                      };
            new TWEEN.Tween(mesh.position, USE_LEGACY_TWEEN_GROUP)
                .to(to, speed)
                .easing(TWEEN.Easing.Quadratic.Out)
                .onComplete(() => resolve())
                .start();
        });
    }

    /**
     * Slides the mesh back to its original pivot position.
     *
     * @param {number} [speed=100] - Duration of the slide-back tween in milliseconds.
     * @return {Promise<void>} Resolves when the slide-back animation completes.
     */
    function slideBackY(speed = 100): Promise<void> {
        return new Promise((resolve) => {
            new TWEEN.Tween(mesh.position, USE_LEGACY_TWEEN_GROUP)
                .to({y: pivot.y, z: pivot.z}, speed)
                .easing(TWEEN.Easing.Quadratic.InOut)
                .onComplete(() => resolve())
                .start();
        });
    }

    /**
     * Sequence to open the door: lift up, swing along arc, then slide out.
     *
     * @param {number} [speed=300] - Base duration in milliseconds.
     * @return {Promise<void>} Resolves when the open sequence completes.
     */
    const openDoor = (speed = 300) =>
        liftUp(speed * 0.2)
            .then(() => arcTween(closedRad, openRad, speed))
            .then(() => slideOutY(speed * 0.9));

    /**
     * Sequence to close the door: swing along arc in closing mode, then slide back.
     *
     * @param {number} [speed=500] - Base duration in milliseconds.
     * @return {Promise<void>} Resolves when the close sequence completes.
     */
    const closeDoor = (speed = 500) =>
        arcTween(openRad, closedRad, speed, true).then(() =>
            slideBackY(speed * 0.9)
        );

    return {
        open: [openDoor],
        close: [closeDoor],
    };
};
